<template>
  <dialog-scroll-lock-loader
    v-if="designId"
    width="960" content-class="fill-height grey lighten-5 overflow-auto d-flex flex-column relative"
    :loading="loadStatus.pending"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :value="true"
    v-bind="$attrs" v-on="$listeners"
  >

    <error-dialog :status="loadStatus" />
    <error-dialog :status="createStatus" />

    <div class="d-flex pa-3">
      <v-btn
        class="px-2"
        text color="primary"
        :disabled="getOrgSkinStatus.pending"
        :loading="createStatus.pending"
        @click="create"
      >
        <v-icon class="mr-2" v-html="mdiPencil" />
        Use this design
        <v-icon class="ml-2" v-html="mdiChevronRight" />
      </v-btn>
      <v-spacer />
      <template v-if="design">
        <design-org-admin-btn v-if="global.profile?.org_admin" scope="card" :target-id="designId" />
        <design-admin-btn v-else-if="global.profile?.admin" :design-id="designId" />
      </template>
      <v-btn icon @click="$emit('input', false)">
        <v-icon v-html="mdiClose" />
      </v-btn>
    </div>

    <v-divider />

    <div v-if="!loadStatus.pending" class="flex-grow-1 relative">

      <!-- <card-background v-if="design?.skin?.page_background_img" style="z-index: -1;" :src="design.skin.page_background_img" /> -->

      <!-- Note that we explicitly use 'scroll' instead of 'auto' to address a bug where an infinite resize loop could occur if an external mouse was plugged in, since the presence of the scroll bar would reposition the elements -->
      <template v-if="design">
        <v-row class="ma-0">
          <v-col cols="8" sm="5" md="6" class="pa-6 mx-auto overflow-hidden">
            <v-responsive class="mx-auto overflow-visible" style="width: 100%;" :aspect-ratio="3/4">
              <envelope-order-preview
                class="fill-height"
                :order="preview"
                @click="() => getOrgSkinStatus.pending || create()"
              />
            </v-responsive>
          </v-col>

          <v-col cols="12" sm="7" md="6" class="pa-6 pt-3 pt-sm-6">
            <div class="d-flex align-center">
              <v-icon color="ec-dark-blue" v-html="iconPathCardUnknownType" />
              <div class="ml-2 ec-dark-blue--text title" style="line-height: 1.2em;">What type of card do you want to send?</div>
            </div>
            <v-radio-group class="pl-8" hide-details :value="type" @change="v => type = v">
              <v-radio class="mb-9" label="Personal card" value="PERSONAL">
                <template v-slot:label>
                  <div class="mr-6">
                    <div>Personal card</div>
                    <div class="text--label-active">Signed by you only</div>
                  </div>
                  <v-spacer />
                  <v-icon v-html="iconPathCardPersonal" />
                </template>
              </v-radio>
              <v-radio class="mb-9" label="Group card" value="GROUP">
                <template v-slot:label>
                  <div class="mr-6">
                    <div>Group card</div>
                    <div class="text--label-active">Invite contributors to sign</div>
                  </div>
                  <v-spacer />
                  <v-icon v-html="iconPathCardGroup" />
                </template>
              </v-radio>
              <v-radio label="Event invitation" value="INVITATION">
                <template v-slot:label>
                  <div class="mr-6">
                    <div>Event invitation</div>
                    <div class="text--label-active">Includes RSVPs</div>
                  </div>
                  <v-spacer />
                  <v-icon v-html="iconPathCardInvitation" />
                </template>
              </v-radio>
            </v-radio-group>

            <div class="mt-10 mt-sm-16 d-flex justify-center justify-sm-start">
              <v-btn
                color="primary"
                :loading="createStatus.pending"
                :disabled="getOrgSkinStatus.pending"
                @click="create"
              >
                <v-icon class="mr-2" v-html="mdiPencil" />
                Use this design
                <v-icon class="ml-2" v-html="mdiChevronRight" />
              </v-btn>
            </div>

            <div v-if="$vuetify.breakpoint.mdAndUp && (design.top_tags || []).length" class="mt-16 pt-0 pt-sm-8 pt-lg-16 overflow-hidden">
              <div class="text--label-active grey--text">Find similar cards</div>
              <div class="mx-n2">
                <v-btn
                  v-for="tag in design.top_tags" :key="tag"
                  v-if="![ 'invitation', 'greeting card' ].includes(tag.toLowerCase())"
                  outlined small rounded dark color="primary" exact
                  class="ma-2"
                  :to="{ params: { selectedId: undefined }, query: { ...$route.query, q: tag } }"
                  v-html="tag"
                />
              </div>
            </div>
          </v-col>
        </v-row>

        <v-divider class="mt-6" />

        <div class="grey lighten-3 pb-3">
          <catalog-design-details class="pa-6" :design-id="designId" :design="design" />

          <div v-if="$vuetify.breakpoint.smAndDown" class="px-6 mt-n2 mb-6 overflow-hidden">
            <template v-if="(design.top_tags || []).length">
              <div class="mt-6 text--label-active grey--text">Find similar cards</div>
              <div class="mx-n2">
                <v-btn
                  v-for="tag in design.top_tags" :key="tag"
                  outlined small rounded dark color="primary" exact
                  class="ma-2"
                  :to="{ params: { selectedId: undefined }, query: { ...$route.query, q: tag } }"
                  v-html="tag"
                />
              </div>
            </template>
          </div>

          <div class="px-6 text--label-active grey--text mb-2">Customize the theme – add backgrounds, confetti, music, stamps, fonts, colors, and more</div>
          <div class="d-flex pb-3 px-6" style="margin-left: -12%;">
            <outline-laptop style="width: 81.5%;">
              <v-lazy>
                <skin-preview
                  class="absolute-fill"
                  from="Example sender"
                  to="Example recipient"
                  mode="preview"
                  :skin="skin"
                  :card="preview"
                  @click.native="create"
                />
              </v-lazy>
            </outline-laptop>
            <outline-phone style="width: 18.5%;" vertical>
              <v-lazy>
                <skin-preview
                  class="absolute-fill"
                  from="Example sender"
                  to="Example recipient"
                  mode="preview"
                  :skin="skin"
                  :card="preview"
                  @click.native="create"
                />
              </v-lazy>
            </outline-phone>
          </div>
        </div>

        <v-divider />

        <div class="pa-3">
          <v-btn
            text color="primary"
            :disabled="getOrgSkinStatus.pending"
            :loading="createStatus.pending"
            @click="create"
          >
            <v-icon class="mr-2" v-html="mdiPencil" />
            Use this design
            <v-icon class="ml-2" v-html="mdiChevronRight" />
          </v-btn>
        </div>
      </template>

      <!--
      <div v-else class="flex-grow-1 d-flex justify-center align-center pa-3">
        <div class="text-center">
          <div class="title grey--text">Card not found</div>
          <div class="mt-8 mb-16">
            Think you're seeing this message in error? Please let us know!<br>
            <a href="mailto:support@ellacard.com">support@ellacard.com</a>
          </div>
          
          <v-btn class="mb-16" style="text-transform: none;" text color="pink" exact :to="{ name: 'catalog' }">
            <v-icon class="mr-1" v-html="mdiCards" />View all cards
          </v-btn>
        </div>
      </div>
      -->
    </div>

  </dialog-scroll-lock-loader>
</template>


<script>
import CardBackground from '@/components/CardBackground.vue';
import CardPreview from '@/components/CardPreview.vue';
import CatalogDesignDetails from '@/components/CatalogDesignDetails.vue';
import Envelope from '@/components/Envelope.vue';
import EnvelopeOrderPreview from '@/components/EnvelopeOrderPreview.vue';
import OutlineLaptop from '@/components/OutlineLaptop.vue';
import OutlinePhone from '@/components/OutlinePhone.vue';
import SkinPreview from '@/components/SkinPreview.vue';
import DialogScrollLockLoader from '@/components/DialogScrollLockLoader.vue';
import { orgSkin } from '@/mixins/org-skin.js';
import { blankDesignId, nullCoverDesignId, publicDesign } from '@/mixins/public-design.js';
import { buildDefaultOrder, updateOrderDesign, updateOrderType } from '@/utils/edit-order.js';
import { findGroupVideoBlob } from '@/utils/group-video.js';
import { DEFAULT } from '@/utils/skin.js';
import uuid4 from '@/utils/uuid.js';
import iconPathCardUnknownType from 'raw-loader!@/assets/icon-path-card-unknown-type.txt';
import iconPathCardGroup from 'raw-loader!@/assets/icon-path-card-group.txt';
import iconPathCardInvitation from 'raw-loader!@/assets/icon-path-card-invitation.txt';
import iconPathCardPersonal from 'raw-loader!@/assets/icon-path-card-personal.txt';
import { mdiCards, mdiClose, mdiSwapHorizontalVariant, mdiPencil, mdiChevronRight } from '@mdi/js';


function getType(design) {
  for (const page of design.pages)
    for (const blob of page.blobs)
      if (blob.type == 'TEXT' && blob.text.event_link)
        return 'INVITATION';
  return null;
}


const PRIMARY_SKIN_KEYS = [ 'env_background_color', 'env_font', 'env_font_color', 'env_stamp', 'music', 'open_effect', 'page_background_color', 'page_background_img' ];


export default {
  name: 'catalog-design',

  components: {
    CardBackground,
    CardPreview,
    CatalogDesignDetails,
    Envelope,
    EnvelopeOrderPreview,
    OutlineLaptop,
    OutlinePhone,
    SkinPreview,
    DialogScrollLockLoader,
    DesignAdminBtn: () => import('@/components/DesignAdminBtn.vue'),
    DesignOrgAdminBtn: () => import('@/components/DesignOrgAdminBtn.vue')
  },

  mixins: [ orgSkin, publicDesign ],

  props: [ 'designId', 'header' ],

  data() {
    return {
      orgSkin: null,
      design: null,
      type: null,
      loadStatus: this.$track('load'),
      createStatus: this.$track('create'),
      getOrgSkinStatus: this.$track('getOrgSkin')
    };
  },

  computed: {
    skin() {
      return Object.assign({}, DEFAULT, this.design?.skin || {}, this.orgSkin || {});
    },

    preview() {
      if (!this.design)
        return;
      const order = buildDefaultOrder();
      order.skin = _.cloneDeep(this.skin);
      updateOrderDesign(order, this.designId, this.design);
      updateOrderType(order, this.type || 'GROUP');
      return order;
    }
  },

  created() {
    Object.assign(this, { iconPathCardUnknownType, iconPathCardGroup, iconPathCardInvitation, iconPathCardPersonal, mdiCards, mdiClose, mdiSwapHorizontalVariant, mdiPencil, mdiChevronRight });
  },

  methods: {
    async load() {
      this.design = null;
      try {
        this.design = _.cloneDeep(await this.getPublicDesign(this.designId));
        this.design.skin ||= {};

        if (this.header?.order?.skin && !PRIMARY_SKIN_KEYS.some(x => this.design.skin[x] != DEFAULT[x]))
          // This design's skin is set entirely to the defaults, use the skin from the header's example order instead
          for (const key of PRIMARY_SKIN_KEYS)
            this.design.skin[key] = this.header.order.skin[key];
        this.type = this.header?.preselect_type || this.global.lastSelectedType || getType(this.design);
        document.title = this.design.name + ' | ' + document.title;
      } catch (e) {
        if (![ 'NotFound', 'InvalidInput' ].includes(e.type))
          throw e;
      }
      this.$emit('loaded');
    },

    async create() {
      const order = buildDefaultOrder();
      order.skin = _.cloneDeep(this.skin);
      updateOrderDesign(order, this.designId, this.design);
      updateOrderType(order, this.type || 'GROUP');

      // Always add an extra page to make sure the user knows that cards can have multiple pages
      order.pages.push({ id: uuid4(), blobs: [] });

      if (this.type)
        order.dirty.push('type');

      const hash = { PERSONAL: '#design', GROUP: '#design', INVITATION: '#event' }[this.type] || '#type';
      const params = { orderId: 'new', order, initDirty: true, routeOnClose: this.$routeAssign({}) };
      this.$router.push({ name: 'project', params, hash });
    }
  },

  watch: {
    designId: {
      immediate: true,
      async handler() {
        this.load();
      }
    },

    'global.profile.org_id': {
      immediate: true,
      async handler(v) {
        this.orgSkin = await this.getOrgSkin(v);
      }
    },

    type: {
      handler(v) {
        if (v)
          this.global.lastSelectedType = v;
      }
    }
  }
}
</script>
